'use client'

import { createContext, type Dispatch, type RefObject, type SetStateAction } from 'react'
import type { Atom } from '@repo/tanstack/store'
import type { LucideIcon } from '@repo/ui/icons'

import type {
  ColumnDef,
  ColumnOrderState,
  ColumnSizingState,
  RowData,
  SortingState,
  Table,
  TableColumnPinningState,
  TableOptions,
} from '../tanstack-table'
import type { ColumnSettingDefs, ColumnSettings, ItemsShownCount } from './merge-table-settings'
import type { TableSettingsSaveStatus } from './use-table-user-settings'

export interface ITableStateContext<TData extends RowData = RowData, TValue = unknown> {
  bulkActions?: TableBulkActionsOptions<TData>
  columnOrder: ColumnOrderState
  columnPinning: TableColumnPinningState
  columns: ColumnDef<TData, TValue>[]
  /** The columns each per-column setting applies to. */
  columnSettingDefs: ColumnSettingDefs
  /**
   * Every column's Field selection, Items shown and Display choices; a cell reads its own with
   * `useFieldSelection`, `useItemsShown` and `useDisplayChoice`.
   */
  columnSettings: Atom<ColumnSettings>
  columnSizing: ColumnSizingState
  columnVisibility: Record<string, boolean>
  /** Whether table settings mark something New: a column, or a Column field of a shown column. */
  hasNewSettings: boolean
  initialColumnOrder: ColumnOrderState
  isSortPending: boolean
  loadedRowCount: number
  /** Columns table settings mark New (`isMarkedNew`). */
  newColumnIds: ReadonlySet<string>
  /** The Column fields table settings mark New (`isMarkedNew`), for every column with fields. */
  newFieldIds: Readonly<Record<string, ReadonlySet<string>>>
  onSortingChange: Dispatch<SetStateAction<SortingState>>
  resetSettings: () => void
  retrySave: () => void
  saveStatus: TableSettingsSaveStatus
  setColumnOrder: (_updater: SetStateAction<ColumnOrderState>) => void
  setColumnPinning: (_updater: SetStateAction<TableColumnPinningState>) => void
  setColumnSizing: (_updater: SetStateAction<ColumnSizingState>) => void
  setColumnVisibility: (_updater: SetStateAction<Record<string, boolean>>) => void
  setData: Dispatch<SetStateAction<TData[]>>
  /**
   * Sets one of a column's Display choices to one of its options, saved with the view's columns
   * like any other column edit.
   */
  setDisplayChoice: (_columnId: string, _choiceId: string, _value: string) => void
  /**
   * Shows `fieldIds` of a column's Column fields, in code order; listing none shows the first.
   * Saved with the view's columns like any other column edit.
   */
  setFieldSelection: (_columnId: string, _fieldIds: readonly string[]) => void
  /** Sets a List cell's Items shown, saved with the view's columns like any other column edit. */
  setItemsShown: (_columnId: string, _count: ItemsShownCount) => void
  setSorting: Dispatch<SetStateAction<SortingState>>
  sorting: SortingState
  sorts: string[]
  table: Table<TData, object>
  /** The rendered table, so the settings panel can reveal a column in it. */
  tableRef: RefObject<HTMLTableElement | null>
}

export type TableBulkAction<TData extends RowData = RowData> = {
  getDisabledReason?: (_context: TableBulkActionContext<TData>) => string | undefined
  icon: LucideIcon
  id: string
  label: string
  onAction: (
    _context: TableBulkActionContext<TData>,
  ) => Promise<TableBulkActionResult | void> | TableBulkActionResult | void
}

export type TableBulkActionContext<TData extends RowData = RowData> = {
  clearActionTarget: () => void
  rowIds: readonly string[]
  rows: readonly TData[]
}

export type TableBulkActionResult =
  | {
      clearSelection?: boolean
      message?: string
      status: 'success'
    }
  | {
      message: string
      retry?: boolean
      status: 'error'
    }

export type TableBulkActionsOptions<TData extends RowData = RowData> = {
  actions: readonly TableBulkAction<TData>[]
  getRowId: NonNullable<TableOptions<TData>['getRowId']>
  itemLabel: {
    plural: string
    singular: string
  }
  selectionScopeKey: string
}

export type TableStateContextValue = Omit<
  ITableStateContext,
  'bulkActions' | 'columns' | 'setData' | 'table'
> & {
  bulkActions?: unknown
  columns: unknown
  setData: unknown
  table: unknown
}

export const TableStateContext = createContext<null | TableStateContextValue>(null)
